import {
    FormControlLabel,
    Grid,
    LinearProgress,
    Radio,
    RadioGroup,
} from '@mui/material'
import { FormFieldSelect } from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import { SelectEmpresa, SelectFuncionario } from 'global/components/Selects'
import Dialog from 'global/guides/Dialog'
import { useTransmit } from 'hooks/useTransmit'
import { hasPermission } from 'hooks/useBackendMenu'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { funcionario as funcionarioApi } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { ExportarService as useService } from '../../service/exportar'

type Formato = 'pdf' | 'xls' | 'csv'

type ExportarMessage =
    | { status: 'PROCESSANDO'; progresso: number }
    | { status: 'CONCLUIDO'; filename: string }
    | { status: 'ERRO'; message: string }

const ExportarFuncionario = () => {
    const navigate = useNavigate()
    const toggle = () => navigate('/cadastros/funcionarios')
    const funcionario = useStore(state => state.employee)
    const empresaId = useStore(state => state.companyId)
    const { handleExportarFicha, isCarregando } = useService()
    const [exportarTodos, setExportarTodos] = useState(true)
    const [formato, setFormato] = useState<Formato>('xls')
    const [progresso, setProgresso] = useState<number | null>(null)

    const subscribe = useTransmit<ExportarMessage>({
        onMessage: async (message, unsubscribe) => {
            if (message.status === 'PROCESSANDO') {
                setProgresso(message.progresso)
                return
            }

            if (message.status === 'CONCLUIDO') {
                try {
                    await funcionarioApi.exportar.file(message.filename)
                } catch (err) {
                    toast.error('Erro ao baixar o arquivo. Tente novamente.')
                    console.error('[Exportar] falha no download:', err)
                }
                await unsubscribe()
                setProgresso(null)
                return
            }

            if (message.status === 'ERRO') {
                toast.error(message.message)
                await unsubscribe()
                setProgresso(null)
            }
        },
    })

    const handleExportarTodos = async () => {
        setProgresso(0)
        try {
            const { cod } = await funcionarioApi.exportar.post<
                { empresaId: number; formato: Formato },
                { cod: string }
            >({ empresaId, formato })
            await subscribe(cod)
        } catch {
            toast.error('Erro ao iniciar exportação. Tente novamente.')
            setProgresso(null)
        }
    }

    const isProcessando = progresso !== null

    return (
        <Dialog.Root
            loading={isCarregando || isProcessando}
            onClose={toggle}
            onSubmit={() => {
                if (exportarTodos) {
                    handleExportarTodos()
                } else {
                    handleExportarFicha(funcionario!, formato)
                }
            }}
            open={hasPermission(['read', 'ExportarFuncionários'])}
            width="48rem"
            height="fit-content"
        >
            <Dialog.Title>
                Exportar funcionário
                <IconeAjuda>
                    Os dados incluídos no arquivo são:
                    {[
                        'Matrícula',
                        'Nome',
                        'Cargo/Função',
                        'PIS',
                        'CPF',
                        'Data de admissão',
                    ].map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                    <br />
                    No entanto, é possível exportar apenas a ficha do
                    funcionário, que contém todos os dados cadastrados.
                </IconeAjuda>
            </Dialog.Title>
            <Dialog.Content
                sx={{
                    width: '100%',
                    overflow: 'auto',
                    p: 2,
                }}
                dividers
            >
                <Grid container spacing={2} mb={4}>
                    <Grid item xs={6}>
                        <SelectEmpresa width="100%" size="medium" />
                    </Grid>
                    <Grid
                        item
                        xs={6}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <RadioGroup
                            aria-label="exportar"
                            defaultValue="todos"
                            name="exportar"
                            onChange={e => {
                                setExportarTodos(e.target.value === 'todos')
                            }}
                            row
                        >
                            <FormControlLabel
                                control={<Radio />}
                                label="Exportar todos"
                                value="todos"
                            />
                            <FormControlLabel
                                control={<Radio />}
                                label="Exportar ficha"
                                value="ficha"
                            />
                        </RadioGroup>
                    </Grid>
                    <Grid item xs={12}>
                        {!exportarTodos && <SelectFuncionario width="100%" />}
                    </Grid>
                    <Grid item xs={12}>
                        <FormFieldSelect
                            required
                            label="Formato de exportação"
                            name="formato"
                            fullWidth
                            defaultValue={'xls'}
                            options={[
                                {
                                    value: 'xls',
                                    label: 'Planilha (XLS)',
                                },
                                {
                                    value: 'pdf',
                                    label: 'Documento (PDF)',
                                },
                                {
                                    value: 'csv',
                                    label: 'Texto (CSV)',
                                },
                            ]}
                            onChange={e => {
                                setFormato(e.target.value as Formato)
                            }}
                        />
                    </Grid>
                    {isProcessando && (
                        <Grid item xs={12}>
                            <LinearProgress
                                variant="determinate"
                                value={progresso ?? 0}
                                sx={{ borderRadius: 1 }}
                            />
                        </Grid>
                    )}
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton disabled={isProcessando} />
                <Dialog.Actions.PrimaryButton disabled={isProcessando}>
                    {isProcessando ? 'Exportando...' : 'Exportar'}
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

export default ExportarFuncionario
